import React, { useState } from 'react';
import api from '../../services/api';

// Lets a signed-in admin change their own password. Requires the current one,
// so a walk-up on an unlocked session cannot silently take over the account.
const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  if (!isOpen) return null;

  const close = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    setDone(false);
    setSaving(false);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      setError('Please choose a password of at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('Those passwords do not match.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      setDone(true);
    } catch (err) {
      setError(
        err.response?.data?.detail || 'Could not update your password. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        {done ? (
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900">Password updated</h3>
            <p className="mt-2 text-sm text-gray-600">
              Your new password is active. Use it next time you sign in.
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-5 w-full rounded-lg bg-orange-500 px-4 py-2 font-medium text-white hover:bg-orange-600"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-gray-900">Change your password</h3>
            <p className="mt-1 text-sm text-gray-500">
              Enter your current password, then choose a new one.
            </p>

            <form onSubmit={submit} className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Current password
                </label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => {
                    setCurrent(e.target.value);
                    if (error) setError(null);
                  }}
                  required
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  New password
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={next}
                  onChange={(e) => {
                    setNext(e.target.value);
                    if (error) setError(null);
                  }}
                  required
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Confirm new password
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                    if (error) setError(null);
                  }}
                  required
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-orange-400 focus:outline-none"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={close}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-60"
                >
                  {saving ? 'Updating...' : 'Update password'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
